// @flow
import { createRef, useState } from "react";
import { css } from "styled-components";
import {
  Modal,
  mediumParagraph,
  regularHeading,
  largeHeading,
  media,
} from "@nested/brand";
import { Button, ButtonLink } from "@nested/component-library";
import {
  Heading,
  QuestionWrapper,
  RadioButtons,
  type RadioButtonsOptions,
} from "../components";
import {
  type QuestionProps,
  type NextFunction,
} from "../components/MultiStageForm";
import { useDesktopFocus } from "../hooks/useDesktopFocus";
import { validatePresence } from "./validators";

const LETTING_FORM = "https://forms.gle/re9GKvXycZGp6P3k7";

const headingStyle = css`
  ${regularHeading}
  margin-top: 0px;

  ${media.desktop`
    ${largeHeading}
    font-size: 40px;
  `}
`;

const validate = validatePresence("customerIntent", "Please select an option");

type CustomerIntentIncLettingValue = CustomerIntentValue | "LETTING";

export const options: RadioButtonsOptions<CustomerIntentIncLettingValue> = [
  {
    value: "ALREADY_ON_THE_MARKET",
    label: "Already on the market",
  },
  {
    value: "READY_TO_SELL",
    label: "Ready to sell",
  },
  {
    value: "THINKING_ABOUT_IT",
    label: "Thinking about selling",
  },
  {
    value: "LETTING",
    label: "Looking to let your property",
  },
  {
    value: "LOOKING_TO_BUY",
    label: "Looking to buy",
  },
  {
    value: "JUST_CURIOUS",
    label: "Just curious",
  },
];

type Props = {
  ...QuestionProps<"customerIntent">,
  initialModalOpen?: boolean,
};
export const LookingToBuyModal = ({
  next,
  open,
  onClose,
}: {
  open: boolean,
  next: NextFunction<"customerIntent">,
  onClose: () => void,
}) => (
  <Modal data-test="looking-to-buy-modal" open={open} onClose={onClose}>
    <h1 css={headingStyle}>Do you need to sell too?</h1>
    <p css={mediumParagraph}>
      Nested are great at selling homes and we can even negotiate your purchase.
      If you're a first-time buyer, browse our homes instead.
    </p>
    <Button
      css={css`
        margin-right: 10px;
        margin-bottom: 10px;
      `}
      type="primary"
      to="https://nested.typeform.com/to/qEFG2C"
      onClick={() => next(null)}
    >
      Yes, I have a home to sell
    </Button>
    <ButtonLink
      css={css`
        margin-bottom: 10px;
      `}
      to="/for-sale"
    >
      Show me homes to buy
    </ButtonLink>
  </Modal>
);

export const CustomerIntent = ({
  ActionButtons,
  BackButton,
  initialValue,
  next,
  valid,
  validationErrors,
  initialModalOpen = false,
}: Props) => {
  const [customerIntent, setCustomerIntent] = useState(initialValue);
  const [modalOpen, setModalOpen] = useState(initialModalOpen);

  const ref = createRef();
  useDesktopFocus(ref);

  return (
    <>
      <QuestionWrapper
        next={(e) => {
          if (customerIntent === "LOOKING_TO_BUY") {
            e.preventDefault();
            setModalOpen(true);
            return;
          }
          if (customerIntent === "LETTING") {
            e.preventDefault();
            window.location.assign(LETTING_FORM);
            return;
          }
          next(customerIntent, validate);
        }}
        dataTest="where-in-your-home-move-are-you-question"
      >
        <BackButton />
        <Heading>Great, Nested is in your area! Are you…</Heading>
        <RadioButtons
          data-test="customer-intent-radios"
          errorMessage={validationErrors.customerIntent}
          hasErrors={!valid}
          name="where-in-your-home-move"
          onChange={setCustomerIntent}
          options={options}
          ref={ref}
          value={customerIntent}
        />
        <ActionButtons />
      </QuestionWrapper>
      <LookingToBuyModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        next={next}
      />
    </>
  );
};
